/**
 * Unit 1.4 · IMPL.A: TanStack table orchestrator for the Clients list.
 *
 * Bead: insurescope-11zkc.2.4.3.1. Fat hook / thin component: owns the
 * query (keepPreviousData + sticky total), the five-key sort state, the
 * branch discriminator, and the interactive-row open affordance (mouse +
 * keyboard parity with the interactive-descendant + portal short-circuits
 * mirrored from `useRowClickHandler`; the documents-list original is typed
 * to ILibraryRow's mode matrix, so the guards are mirrored rather than the
 * hook imported). Mirror: `useDocumentListTable`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
    getCoreRowModel,
    useReactTable,
    type Table,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { getClients, type ClientSortKey, type IClient } from "@/api";

import { getClientListColumns } from "../components/clientListColumns";

export type ClientListBranch =
    | "loading"
    | "error"
    | "empty-search"
    | "empty-library"
    | "populated";

export const CLIENT_SORT_OPTIONS: ReadonlyArray<{
    key: ClientSortKey;
    label: string;
}> = [
    { key: "recently_active", label: "Recently active" },
    { key: "name_asc", label: "Name (A → Z)" },
    { key: "name_desc", label: "Name (Z → A)" },
    { key: "most_documents", label: "Most documents" },
    // Owned + shared-with-me conversation count; matches the Chats column.
    { key: "most_polly_chats", label: "Most Polly chats" },
];

// Any descendant that handles its own click MUST NOT trigger row
// navigation (kebab trigger, menu items, dialogs). Mirrors
// useRowClickHandler's selector + portal guard. INTENTIONAL deltas from the
// documents original: + [role="dialog"] (belt-and-suspenders beside the
// containment check), - [data-slot="checkbox"] (no checkboxes here); do
// not "resync" these away.
const INTERACTIVE_DESCENDANT_SELECTOR =
    'button, a, [role="menuitem"], [role="menu"], [role="dialog"], [data-slot="dropdown-menu-trigger"]';

export interface UseClientListTableOptions {
    /** Already-debounced search value (drives `q` + the empty-search branch). */
    searchQuery: string;
    /** Renders the per-row kebab (IMPL.C's ClientRowActions). */
    renderActions: (client: IClient) => React.ReactNode;
    pageSize?: number;
}

export interface UseClientListTableResult {
    table: Table<IClient>;
    branch: ClientListBranch;
    rows: IClient[];
    /** True for ANY in-flight fetch (initial + page/sort refetches); drives
     * the document-library fetch fade (Unit 4 parity brief, D1/D7). */
    isFetching: boolean;
    sortKey: ClientSortKey;
    setSortKey: (key: ClientSortKey) => void;
    refetch: () => void;
    /** Row open-affordance handlers (attach to the populated `<TableRow>`). */
    handleRowClick: (
        client: IClient
    ) => (event: React.MouseEvent<HTMLTableRowElement>) => void;
    handleRowKeyDown: (
        client: IClient
    ) => (event: React.KeyboardEvent<HTMLTableRowElement>) => void;
}

export function useClientListTable({
    searchQuery,
    renderActions,
    pageSize = 50,
}: UseClientListTableOptions): UseClientListTableResult {
    const navigate = useNavigate();
    const [page, setPageState] = useState(1);
    const [sortKey, setSortKeyState] =
        useState<ClientSortKey>("recently_active");

    // A new search restarts from the first page.
    useEffect(() => {
        setPageState(1);
    }, [searchQuery]);

    const { data, isPending, isError, isFetching, isPlaceholderData, refetch } =
        useQuery({
            queryKey: ["clients", page, pageSize, searchQuery, sortKey],
            queryFn: () =>
                getClients(page, pageSize, {
                    q: searchQuery || null,
                    sort_by: sortKey,
                }),
            // Keep the previous page on screen during refetches; no flash of
            // skeleton between pages/sorts.
            placeholderData: keepPreviousData,
        });

    // Sticky total: avoid the pager collapsing to 0 mid-refetch (mirrors the
    // legacy page's rowCountRef).
    const totalRef = useRef(0);
    if (data?.total !== undefined) {
        totalRef.current = data.total;
    }
    const total = totalRef.current;
    const rows = useMemo(() => data?.items ?? [], [data?.items]);

    // Clamp an out-of-range page back into bounds once the result set shrinks
    // — e.g. deleting the last row on the final page. Without it the query
    // holds an empty out-of-range slice: the branch below would flash the
    // "No clients yet" empty state while clients still exist, and the pager
    // (gated on the populated branch) offers no way back. Mirrors the single
    // clamp invariant in useClientConversationsTab.
    useEffect(() => {
        if (data && !isPlaceholderData && page > Math.max(1, data.pages)) {
            setPageState(Math.max(1, data.pages));
        }
    }, [data, isPlaceholderData, page]);

    const setSortKey = useCallback((key: ClientSortKey) => {
        setSortKeyState(key);
        // Sort changes restart from the first page.
        setPageState(1);
    }, []);

    const navigateToClient = useCallback(
        // Land directly on the Library tab (the default) rather than the
        // bare client path, which would only redirect there.
        (client: IClient) =>
            navigate(`/organizations/clients/${client.id}/library`),
        [navigate]
    );

    const handleRowClick = useCallback(
        (client: IClient) => (event: React.MouseEvent<HTMLTableRowElement>) => {
            const target =
                event.target instanceof HTMLElement ? event.target : null;
            if (!target) return;
            // Portaled descendants (dialogs/menus) bubble through the
            // React tree but live outside the row's DOM subtree.
            if (event.currentTarget && !event.currentTarget.contains(target)) {
                return;
            }
            if (target.closest(INTERACTIVE_DESCENDANT_SELECTOR)) return;
            navigateToClient(client);
        },
        [navigateToClient]
    );

    const handleRowKeyDown = useCallback(
        (client: IClient) =>
            (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                if (event.key !== "Enter") return;
                // Only when the row itself is focused; Enter inside the
                // kebab/menus belongs to them.
                if (event.target !== event.currentTarget) return;
                navigateToClient(client);
            },
        [navigateToClient]
    );

    const columns = useMemo(
        () => getClientListColumns({ renderActions }),
        [renderActions]
    );

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.id),
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        rowCount: total,
        enableColumnResizing: false,
        // Controlled pagination so DataTablePagination's table-driven
        // next/prev/size controls round-trip into the query key.
        state: {
            pagination: { pageIndex: page - 1, pageSize },
        },
        onPaginationChange: (updater) => {
            const prev = { pageIndex: page - 1, pageSize };
            const next =
                typeof updater === "function" ? updater(prev) : updater;
            setPageState(next.pageIndex + 1);
        },
    });

    // `total` (the sticky count), not the current slice, decides
    // populated-ness: a clamp in flight can serve an empty out-of-range slice
    // for a frame, and that must never flash the empty-library copy while
    // clients still exist (mirrors useClientConversationsTab).
    const branch: ClientListBranch = isPending
        ? "loading"
        : isError
          ? "error"
          : total > 0
            ? "populated"
            : searchQuery
              ? "empty-search"
              : "empty-library";

    return {
        table,
        branch,
        rows,
        isFetching,
        sortKey,
        setSortKey,
        refetch,
        handleRowClick,
        handleRowKeyDown,
    };
}
